import React, {useCallback, useRef} from 'react';
import {Avatar, Box, IconButton} from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PropTypes from 'prop-types';
import useNotification from '../../../../../utility/hooks/useNotification';

const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];

const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
            reject(new Error('Failed to read image file.'));
            return;
        }

        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
});

const ApplicantPhotoUpload = ({
    photo,
    alt,
    canUpload,
    onPhotoSelect,
}) => {
    const inputRef = useRef(null);
    const {showErrorNotification} = useNotification();

    const handleFileSelect = useCallback(async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) {
            return;
        }

        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            showErrorNotification('Only JPEG and PNG images are allowed.');
            return;
        }

        if (file.size > MAX_PHOTO_SIZE_BYTES) {
            showErrorNotification('The photo exceeds the 5 MB limit.');
            return;
        }

        try {
            const base64Photo = await fileToBase64(file);
            onPhotoSelect(base64Photo);
        } catch {
            showErrorNotification('Failed to read the selected photo.');
        }
    }, [onPhotoSelect, showErrorNotification]);

    return (
        <Box>
            <Box position="relative" width={120} height={120} mb={2}>
                <Avatar
                    variant="square"
                    sx={{width: 120, height: 120}}
                    alt={alt}
                    src={photo ? `data:image/jpeg;base64,${photo}` : undefined}
                />
                {canUpload && (
                    <>
                        <input
                            ref={inputRef}
                            type="file"
                            accept={ACCEPTED_IMAGE_TYPES.join(',')}
                            hidden
                            onChange={handleFileSelect}
                        />
                        <IconButton
                            aria-label="Upload applicant photo"
                            size="small"
                            color="primary"
                            sx={{
                                position: 'absolute',
                                right: 4,
                                bottom: 4,
                                bgcolor: 'background.paper',
                                boxShadow: 1,
                                '&:hover': {bgcolor: 'background.paper'},
                            }}
                            onClick={() => inputRef.current?.click()}
                        >
                            <PhotoCameraOutlinedIcon fontSize="small"/>
                        </IconButton>
                    </>
                )}
            </Box>
        </Box>
    );
};

ApplicantPhotoUpload.propTypes = {
    photo: PropTypes.string,
    alt: PropTypes.string.isRequired,
    canUpload: PropTypes.bool,
    onPhotoSelect: PropTypes.func.isRequired,
};

export default ApplicantPhotoUpload;
